import { APICurrentUser, APIUserFeatureToggles } from '@ardoq/api-types';
import { userOperations } from '@ardoq/core';
import {
  FeatureContext,
  featureOperations,
  getLocalFeatureFlagOverrides,
} from '@ardoq/features';
import { toPersistentStream } from '@ardoq/rxbeach';
import { isEqual } from 'lodash';
import {
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  startWith,
} from 'rxjs';
import { loadedCurrentUser$ } from './currentUser/currentUser$';

const toFeatureContext = (
  user: APICurrentUser,
  devOverrides?: APIUserFeatureToggles | null
): FeatureContext => {
  const featureContext: FeatureContext = {
    isOrgAdmin: userOperations.hasAdminRole(user),
    isArdoqEmployee: userOperations.isArdoqEmployee(user),
    isTrial: userOperations.isTrial(user),
    features: user.features || {},
  };

  if (devOverrides) {
    featureContext.devOverrides = devOverrides;
  }

  return featureContext;
};

export const featureContext$: Observable<FeatureContext> =
  loadedCurrentUser$.pipe(
    map(user => toFeatureContext(user, getLocalFeatureFlagOverrides())),
    startWith(featureOperations.getEmptyFeatureContext()),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

const internalFeatureContext$ = toPersistentStream(
  'featureContext$',
  featureContext$,
  featureOperations.getEmptyFeatureContext()
);

/**
 * Manual connect to make featureContext available as early as possible
 * because it supersedes the CurrentUser backbone model which is accessed
 * globally without waiting for our state stream to initialize.
 */
internalFeatureContext$.connect();

/**
 * Prefer subscribing to featureContext$
 */
export const getFeatureContext = () => internalFeatureContext$.state;
